import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { PageHero } from "@/components/common/SectionHeading";
import { lookupApplication, uploadDocument } from "@/lib/lnlApi";
import { STATUS_TONE } from "@/lib/lnlConstants";
import { CheckCircle2, Upload, Printer, Copy, FileText } from "lucide-react";
import { toast } from "sonner";

export default function ApplicantDashboard() {
    const { reference } = useParams();
    const [params] = useSearchParams();
    const emailParam = params.get("email") || "";
    const [email, setEmail] = useState(emailParam);
    const [doc, setDoc] = useState(null);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    async function load(e) {
        if (e) e.preventDefault();
        setErr("");
        try { setDoc(await lookupApplication(reference, email)); }
        catch (x) { setErr(x?.response?.data?.detail || "Application not found."); }
    }
    useEffect(() => { if (emailParam) load(); /* eslint-disable-next-line */ }, []);

    async function upload(e, label) {
        const file = e.target.files?.[0];
        if (!file) return;
        setBusy(true);
        try { await uploadDocument(reference, email, label, file); toast.success(`Uploaded ${label}`); await load(); }
        catch (x) { toast.error(x?.response?.data?.detail || "Upload failed."); }
        finally { setBusy(false); e.target.value = ""; }
    }

    return (
        <div data-testid="applicant-dashboard">
            <PageHero eyebrow="Applicant dashboard" title={`Application ${reference}`} description="Track your application, upload missing documents and download a printable summary." breadcrumbs={[{label:"Home",to:"/"},{label:"Applicant Dashboard"}]} />
            <section className="py-10 bg-white">
                <div className="lnl-container max-w-4xl">
                    {!doc && (
                        <form onSubmit={load} className="lnl-card p-6 flex flex-col sm:flex-row gap-3 items-end" data-testid="dashboard-lookup">
                            <div className="flex-1">
                                <label className="text-xs text-lnl-gray font-heading font-semibold">Contact email</label>
                                <input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2.5" type="email" data-testid="dashboard-email" />
                            </div>
                            <button className="lnl-btn-primary" data-testid="dashboard-load">Open dashboard</button>
                            {err && <div className="text-sm text-rose-700">{err}</div>}
                        </form>
                    )}
                    {doc && (
                        <>
                            <div className="lnl-card p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="dashboard-summary">
                                <div>
                                    <div className="text-xs text-lnl-gray">Reference</div>
                                    <div className="font-heading font-bold text-lnl-charcoal inline-flex items-center gap-2">
                                        {doc.reference}
                                        <button onClick={() => { navigator.clipboard.writeText(doc.reference); toast.success("Copied"); }} className="text-lnl-gray hover:text-lnl-green"><Copy className="h-4 w-4" /></button>
                                    </div>
                                </div>
                                <span className={`text-xs px-2 py-1 rounded-md border font-heading font-semibold ${STATUS_TONE[doc.status] || ""}`} data-testid="dashboard-status">{doc.status}</span>
                            </div>

                            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                                <div className="lg:col-span-2 space-y-4">
                                    <div className="lnl-card p-5">
                                        <div className="lnl-eyebrow">Next steps</div>
                                        <ul className="mt-3 text-sm text-lnl-charcoal space-y-2">
                                            <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-lnl-green mt-0.5" /> LNL Admissions will review your submitted documents.</li>
                                            <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-lnl-green mt-0.5" /> Missing documents can be uploaded below.</li>
                                            <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-lnl-green mt-0.5" /> You'll be contacted using the email you provided.</li>
                                        </ul>
                                    </div>
                                    <div className="lnl-card p-5">
                                        <div className="lnl-eyebrow">Uploaded documents</div>
                                        <ul className="mt-3 space-y-2 text-sm">
                                            {(doc.documents || []).map((d) => (
                                                <li key={d.id} className="flex items-center justify-between border-b border-lnl-border pb-2 last:border-0 last:pb-0">
                                                    <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-lnl-green" /> <span>{d.label}</span></div>
                                                    <span className="text-xs text-lnl-gray">{d.original_filename}</span>
                                                </li>
                                            ))}
                                            {!doc.documents?.length && <li className="text-lnl-gray">No documents uploaded yet.</li>}
                                        </ul>
                                        <label className="mt-4 inline-flex items-center gap-2 lnl-btn-secondary cursor-pointer" data-testid="dashboard-upload">
                                            <Upload className="h-4 w-4" /> Upload additional document
                                            <input type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={(e) => upload(e, "Additional document")} disabled={busy} />
                                        </label>
                                    </div>
                                    <div className="lnl-card p-5">
                                        <div className="lnl-eyebrow">Activity</div>
                                        <ol className="mt-3 space-y-2 text-sm">
                                            {(doc.activity || []).slice().reverse().map((a, i) => (
                                                <li key={i} className="border-l-2 border-lnl-green pl-3">
                                                    <div className="text-xs text-lnl-gray">{new Date(a.at).toLocaleString()}</div>
                                                    <div>{a.detail}</div>
                                                </li>
                                            ))}
                                        </ol>
                                    </div>
                                </div>
                                <aside className="space-y-3 h-fit">
                                    <button onClick={() => window.print()} className="lnl-btn-secondary w-full justify-center" data-testid="dashboard-print"><Printer className="h-4 w-4" /> Printable summary</button>
                                    <Link to="/contact" className="lnl-btn-primary w-full justify-center">Contact admissions</Link>
                                </aside>
                            </div>
                        </>
                    )}
                </div>
            </section>
        </div>
    );
}
